import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Создаем мок-объект пользователя для тестирования в браузере
const mockUser = {
  id: 1234567890, // <-- ВАШ ТЕСТОВЫЙ ID
  first_name: 'Unauthorized',
  last_name: 'Unauthorized',
  username: 'Unauthorized',
  language_code: 'en',
  is_premium: true,
};

// Создаем мок-объект для всего Telegram Web App API
const mockWebApp = {
  initDataUnsafe: {
    user: mockUser,
  },
  colorScheme: 'light',
  themeParams: {
    bg_color: '#ffffff',
    text_color: '#000000',
  },
  // Добавьте пустые функции для методов, которые могут вызываться
  onEvent: () => {},
  offEvent: () => {},
  sendData: (data) => console.log('SendData (mocked):', data),
  ready: () => {},
  expand: () => {},
  close: () => {},
};

export const TelegramContext = createContext();

const TelegramProvider = ({ children }) => {
  const [webApp, setWebApp] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Проверяем, доступен ли объект Telegram Web App
    const tg = window.Telegram?.WebApp;

    const findOrCreateUser = async (tgUser) => {
      try {
        // First, check if the user already exists
        try {
          const { data: existingUser } = await axios.get(`/api/users/${tgUser.id}`);

          // If user exists, update only name, avatar, and premium status, but not language
          const updatePayload = {
            telegram_id: `${tgUser.id}`,
            username: tgUser.username,
            name: `${tgUser.first_name || ''} ${tgUser.last_name || ''}`.trim(),
            avatar: tgUser.photo_url,
            is_premium: tgUser.is_premium
          };

          // Update user with existing data
          const { data: updatedUser } = await axios.put(`/api/users/${tgUser.id}`, updatePayload);
          setUser(updatedUser);

        } catch (err) {
          // If user doesn't exist (404) or other error, create a new user with all data including language
          const userDataPayload = {
            telegram_id: `${tgUser.id}`,
            username: tgUser.username,
            // Ваш бэкенд ожидает поле 'name', объединим first_name и last_name
            name: `${tgUser.first_name || ''} ${tgUser.last_name || ''}`.trim(),
            language: tgUser.language_code,
            // Ваш бэкенд принимает 'avatar', передадим photo_url если он есть
            avatar: tgUser.photo_url,
            is_premium: tgUser.is_premium
          };

          // Create new user
          const { data: newUser } = await axios.post('/api/users', userDataPayload);
          setUser(newUser);
        }
      } catch (error) {
        console.error("Ошибка при обработке пользователя:", error);
      }
    };

    if (tg && tg.initDataUnsafe?.user) {
      tg.ready();
      setWebApp(tg);
      // Вызываем нашу новую функцию для реального пользователя
      findOrCreateUser(tg.initDataUnsafe.user);
    } else {
      console.warn('Running in browser mode. Using mock Telegram data.');
      setWebApp(mockWebApp);
      // Вызываем функцию и для мок-данных, чтобы можно было тестировать из браузера
      findOrCreateUser(mockWebApp.initDataUnsafe.user);
    }
  }, []);

  const value = useMemo(() => ({ webApp, user }), [webApp, user]);

  // Не рендерим дочерние компоненты, пока не определились с пользователем
  if (!user) {
    return null; // или можно показать здесь глобальный спиннер загрузки
  }

  return (
      <TelegramContext.Provider value={value}>
        {children}
      </TelegramContext.Provider>
  );
};

export const useTelegram = () => {
  const context = useContext(TelegramContext);
  if (context === undefined) {
    throw new Error('useTelegram must be used within a TelegramProvider');
  }
  return context;
};

export default TelegramProvider;
